import { X } from '@/design-system/icons';
import { cn } from '@/lib';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

interface DialogContextValue {
  onClose: () => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext() {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('Dialog components must be used inside Dialog');
  }

  return context;
}

export interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
}

export function Dialog({
  isOpen,
  onClose,
  children,
  className,
  closeOnBackdrop = true,
  closeOnEscape = true,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      return;
    }

    if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      if (!closeOnEscape) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      onClose();
    };

    dialog.addEventListener('cancel', handleCancel);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [closeOnEscape, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (!closeOnBackdrop) return;
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  const contextValue = useMemo(
    () => ({
      onClose,
    }),
    [onClose]
  );

  return (
    <DialogContext.Provider value={contextValue}>
      <dialog
        ref={dialogRef}
        onClick={handleBackdropClick}
        className={cn(
          'bg-surface fixed top-1/2 left-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg p-0 shadow-xl backdrop:bg-black/50',
          className
        )}
      >
        {children}
      </dialog>
    </DialogContext.Provider>
  );
}

export interface DialogHeaderProps {
  children: ReactNode;
  showClose?: boolean;
  className?: string;
  titleClassName?: string;
}

export function DialogHeader({
  children,
  showClose = false,
  className,
  titleClassName,
}: DialogHeaderProps) {
  const { onClose } = useDialogContext();

  const shouldWrapInHeadline =
    typeof children === 'string' || typeof children === 'number';

  return (
    <header
      className={cn(
        'border-border flex items-center justify-between gap-3 border-b px-6 py-4',
        className
      )}
    >
      {shouldWrapInHeadline ? (
        <h2
          className={cn('text-onsurface text-lg font-semibold', titleClassName)}
        >
          {children}
        </h2>
      ) : (
        children
      )}

      {showClose && (
        <button
          type="button"
          onClick={onClose}
          className="border-border bg-surface text-muted hover:bg-surface-darker inline-flex size-8 items-center justify-center rounded-md border focus:outline-none"
        >
          <X className="size-4" />
        </button>
      )}
    </header>
  );
}

export interface DialogBodyProps {
  children: ReactNode;
  className?: string;
}

export function DialogBody({ children, className }: DialogBodyProps) {
  return <div className={cn('px-6 py-4', className)}>{children}</div>;
}

export const DialogBoddy = DialogBody;

export interface DialogFooterProps {
  children?: ReactNode;
  abortText?: string;
  submitText?: string;
  onSubmit?: () => void;
  className?: string;
  submitButtonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  abortButtonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  submitButtonClassName?: string;
  abortButtonClassName?: string;
}

export function DialogFooter({
  children,
  abortText,
  submitText,
  onSubmit,
  className,
  submitButtonProps,
  abortButtonProps,
  submitButtonClassName,
  abortButtonClassName,
}: DialogFooterProps) {
  const { onClose } = useDialogContext();

  if (children) {
    return (
      <footer
        className={cn(
          'border-border flex justify-end gap-3 border-t px-6 py-3 pt-2 pb-6',
          className
        )}
      >
        {children}
      </footer>
    );
  }

  return (
    <footer
      className={cn(
        'border-border flex justify-end gap-3 border-t px-4 py-3',
        className
      )}
    >
      {/*       <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-muted hover:text-onsurface transition-colors disabled:opacity-50"
        {...abortButtonProps}
      >
        {abortText}
      </button>
      <PrimaryButton
        onClick={onSubmit}
        className={submitButtonClassName}
        {...submitButtonProps}
      >
        {submitText}
      </PrimaryButton> */}

      <button
        type="button"
        onClick={onClose}
        className={cn(
          'border-border bg-surface text-onsurface hover:bg-surface-darker rounded-lg border px-4 py-2 text-sm font-medium focus:outline-none',
          abortButtonClassName
        )}
        {...abortButtonProps}
      >
        {abortText}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        className={cn(
          'bg-primary text-onprimary hover:bg-primary-darker focus:ring-primary rounded-lg border border-transparent px-4 py-2 text-sm font-medium focus:ring-2 focus:outline-none disabled:pointer-events-none disabled:opacity-50',
          submitButtonClassName
        )}
        {...submitButtonProps}
      >
        {submitText}
      </button>
    </footer>
  );
}
